// UGC Video Feature Types

export type AvatarType = 'avatar' | 'talking_photo';

export interface Avatar {
  avatar_id: string;
  name: string;
  thumbnail_url: string;
  provider: string;
  /** When 'talking_photo', use avatar_id as talking_photo_id in generate payload */
  avatar_type?: AvatarType;
  /** HeyGen short demo video shown on hover in the avatar picker. */
  preview_video_url?: string;
  gender?: string;
}

export interface Voice {
  voice_id: string;
  name: string;
  language: string;
  gender: string;
  sample_url?: string;
  /** 'HEYGEN' (native TTS) or 'ELEVENLABS' (custom cloned voice → TTS + audio mode). */
  provider?: string;
  /** True for the customer's own cloned voices (shown in the Custom Voice tab). */
  is_custom?: boolean;
}

export type SegmentAnimIn =
  | 'none' | 'fade'
  | 'slide-left' | 'slide-right' | 'slide-up' | 'slide-down'
  | 'wipe-left'  | 'wipe-right'  | 'wipe-up'   | 'wipe-down'
  | 'zoom-in' | 'pop' | 'spin';
export type SegmentAnimOut =
  | 'none' | 'fade'
  | 'slide-left' | 'slide-right' | 'slide-up' | 'slide-down'
  | 'wipe-left'  | 'wipe-right'  | 'wipe-up'  | 'wipe-down'
  | 'zoom-out' | 'spin';
export type SegmentMotion = 'none' | 'ken-burns-in' | 'ken-burns-out' | 'pan-left' | 'pan-right';

/** One image/video clip on the timeline. */
export interface MediaSegment {
  media_id: string;
  time: number;
  duration: number;
  /** How the clip is scaled inside the canvas frame. Default: 'cover' (fills, may crop). */
  fit?: 'contain' | 'cover';
  /** Zoom multiplier (0.5–3.0). 1.0 = normal, >1 zooms in, <1 zooms out. Default: 1.0. */
  zoom?: number;
  /** Distinguishes stock video clips from still images on the timeline. */
  segment_type?: 'image' | 'video';
  /** Legacy simple fade-in flag (overridden by animation_in when set). */
  fade_in?: boolean;
  /** Legacy simple fade-out flag (overridden by animation_out when set). */
  fade_out?: boolean;
  /** Entry animation for this clip. Defaults to 'none'. */
  animation_in?: SegmentAnimIn;
  /** Duration of the entry animation in seconds (0.1–2.0). Defaults based on animation type. */
  anim_in_duration?: number;
  /** Exit animation for this clip. Defaults to 'none'. */
  animation_out?: SegmentAnimOut;
  /** Duration of the exit animation in seconds (0.1–2.0). Defaults based on animation type. */
  anim_out_duration?: number;
  /** Motion effect spanning the full clip duration (images only). Defaults to 'none'. */
  motion?: SegmentMotion;
  /** Volume for video segments (0–100). Defaults to 0 (muted). */
  volume?: number;
  /** Opacity (0–100). Defaults to 100 (fully opaque). */
  opacity?: number;
  /** Canvas layout (percent of canvas, top-left anchored), set by dragging/resizing the clip
   *  on the editor canvas. When unset, the clip renders centered at its zoom size (legacy). */
  x_pct?: number;
  y_pct?: number;
  width_pct?: number;
  height_pct?: number;
  /** Trim start: seconds to skip from the beginning of the source video. Default: 0. */
  trim_start?: number;
  /** Trim end: seconds to skip from the end of the source video. Default: 0. */
  trim_end?: number;
}

/** A text overlay that appears on the video at a specific time. */
export interface TextOverlay {
  id: string;
  text: string;
  time: number;
  duration: number;
  style?: 'title' | 'subtitle' | 'cta' | 'lower-third';
  position?: 'top' | 'center' | 'bottom';
  /** Entry animation. Defaults to 'none'. */
  animation?: 'none' | 'fade' | 'pop' | 'slide-left' | 'slide-right' | 'slide-up' | 'slide-down' | 'wipe-up' | 'wipe-down' | 'typewriter' | 'bounce' | 'spin';
  /** Duration of the entry animation in seconds (0.1–2.0). Defaults based on animation type. */
  anim_duration?: number;
  /** Exit animation. Defaults to 'none'. */
  animation_out?: 'none' | 'fade' | 'pop' | 'slide-left' | 'slide-right' | 'slide-up' | 'slide-down' | 'wipe-up' | 'wipe-down' | 'bounce' | 'spin';
  /** Duration of the exit animation in seconds (0.1–2.0). Default 0.35. */
  anim_out_duration?: number;
  /** Visual design preset (color, background, stroke, shadow). Independent from captions. */
  design?: 'classic' | 'minimalist' | 'neon' | 'bold' | 'karaoke' | 'fire' | 'shadow' | 'gradient' | 'highlight' | 'pill' | 'cinema';
  /** Font size in vmin units (e.g. 6 = small, 9 = medium, 13 = large). When unset, derived from `style`. */
  fontSize?: number;
  /** Allowlisted Creatomate-supported Google Font family name (e.g. "Montserrat").
   *  When unset, both preview and render use "Open Sans". Anything outside the
   *  allowlist is silently ignored by the backend, so the editor should only set
   *  values from UGC_TEXT_FONTS. */
  fontFamily?: string;
  /** Text box width as percentage of the video container width (10–90). Default 56. */
  width?: number;
  /** Optional background fill behind the text (CSS color string). When unset, no background is shown. */
  backgroundColor?: string;
  /** Exact horizontal center position as percentage of canvas width (0–100). Overrides the position enum x. */
  x_pct?: number;
  /** Exact vertical center position as percentage of canvas height (0–100). Overrides the position enum y. */
  y_pct?: number;
  /** Rotation in degrees (0–360). Default 0. */
  rotation?: number;
  /** Custom text color override (CSS color string). When unset, derived from `design`. */
  color?: string;
  /** Bold text. Default false. */
  bold?: boolean;
  /** Italic text. Default false. */
  italic?: boolean;
  /** Outlined text — a contrasting stroke around the letters for readability. Default false. */
  outline?: boolean;
  /** Custom outline color (CSS color string). Only used when outline is on; when unset the
   *  preview/render picks a contrast color from the fill (black for light fills, white for dark). */
  outlineColor?: string;
  /**
   * Where this overlay came from. 'caption' overlays are auto-generated from the verbatim
   * script (see buildCaptionOverlays) and serve as editable, error-free captions; they can be
   * bulk-regenerated/cleared and suppress the backend's STT auto-caption. 'manual' (default)
   * overlays are user-added and never touched by caption regeneration.
   */
  source?: 'caption' | 'manual';
  /**
   * Groups multiple overlays into one logical unit (e.g. a template with a styled title +
   * subtitle + accent). Overlays sharing a `groupId` move, select, and delete together in the
   * editor while each keeps its own design/size/color. Frontend-only — the backend renders each
   * member as its own text element, so the differing sizes/colors burn into the final video.
   */
  groupId?: string;
}

export type UGCVideoFormat = 'reel' | 'landscape' | 'square';
export type UGCAvatarStyle = 'normal' | 'closeUp' | 'circle';

/** HeyGen-supported aspect ratios. */
export type UGCAspectRatio = '9:16' | '16:9' | '1:1';
/** HeyGen output resolution. 1080p is the backend default (previously 480p hardcoded). */
export type UGCVideoResolution = '720p' | '1080p' | '4k';

/** Maps the user-facing video format to the explicit aspect ratio HeyGen expects. */
export const VIDEO_FORMAT_TO_ASPECT_RATIO: Record<UGCVideoFormat, UGCAspectRatio> = {
  reel: '9:16',
  landscape: '16:9',
  square: '1:1',
};

/** HeyGen rendering engine. Avatar IV is the default; Avatar V uses cross-reference animation for higher quality. */
export type HeyGenEngine = 'avatar_iv' | 'avatar_v';

/** Avatar IV body-motion intensity. Avatar V does not support this — sending it causes a HeyGen validation error. */
export type HeyGenExpressiveness = 'low' | 'medium' | 'high';

export interface UGCAvatarLayout {
  scale?: number;
  offset_x?: number;
  offset_y?: number;
  avatar_style?: UGCAvatarStyle;
  matting?: boolean;
}

export interface GenerateUGCVideoParams {
  customer_id: string;
  /** Required when avatar_type === 'avatar' */
  avatar_id?: string;
  /** Required when avatar_type === 'talking_photo' */
  talking_photo_id?: string;
  voice_id: string;
  /** 'ELEVENLABS' for custom cloned voices (pipeline runs TTS + HeyGen audio mode); omit for HeyGen native TTS. */
  voice_provider?: 'HEYGEN' | 'ELEVENLABS';
  /** Transcript content; sent as "text" in the API payload */
  text: string;
  /** Custom image segments (no longer sent to backend; kept for type compatibility). */
  media_ids?: MediaSegment[];
  video_format?: UGCVideoFormat;
  /** Explicit aspect ratio sent to HeyGen. Derived from video_format if omitted. */
  aspect_ratio?: UGCAspectRatio;
  /** Output resolution. Backend default is 1080p. */
  resolution?: UGCVideoResolution;
  avatar_layout?: UGCAvatarLayout;
  name?: string;
  /** HeyGen rendering engine. Omit for default (avatar_iv); use avatar_v for higher quality. */
  engine?: { type: HeyGenEngine };
  /** Avatar IV only: natural-language description of desired gestures/body motion (max 300 chars). */
  motion_prompt?: string;
  /** Avatar IV only: body motion intensity. */
  expressiveness?: HeyGenExpressiveness;
}

export interface VideoGenerationResponse {
  message: string;
  video_id: string;
  argil_video_id: string | null;
  /** Backend status e.g. Queued, Audio, Video, Compositing, AVATAR_READY, FAILED */
  status: string;
  estimated_time?: number;
}

export interface VideoStatusResponse {
  /** Backend status e.g. Queued, Audio, Video, Compositing, AVATAR_READY, COMPLETED (post-assembly), FAILED (404 returns error only) */
  status: string;
  cloudfront_url?: string;
  /** Avatar video URL when status is AVATAR_READY (backend may send this instead of cloudfront_url). */
  avatar_video_url?: string;
  /** Present when status is AVATAR_READY or COMPLETED; used as timeline length for custom images step. */
  duration_seconds?: number;
  error?: string;
}

/** A single word with its real start/end timestamps (seconds) from the analyze-captions endpoint (Whisper STT, script-aligned). */
export interface CaptionCue {
  word: string;
  start: number;
  end: number;
}

export interface UGCVideoMetadata {
  avatar_id: string;
  voice_id: string;
  media_ids: string[];
  transcript: string;
}

export interface UGCVideoSelections {
  selectedAvatar: Avatar | null;
  selectedVoice: Voice | null;
  /** Timeline segments (media_id + time + duration). Derive selectedMediaIds as .map(s => s.media_id) for grid. */
  timelineSegments: MediaSegment[];
}

export interface UGCVideoDraft {
  draft_id: string;
  title: string;
  saved_at: number; // Unix ms

  // Step 1 — Script
  scriptContent: string;
  activeConfigStep: 1 | 2 | 3 | 4 | 5;

  // Step 2 — AI Model
  selectedAvatarId: string | null;
  selectedAvatarType: AvatarType | null;

  // Step 3 — Voice
  selectedVoiceId: string | null;

  // Format / engine
  selectedVideoFormat: UGCVideoFormat;
  selectedEngine: HeyGenEngine;
  expressiveness: HeyGenExpressiveness;
  motionPrompt: string;
  selectedResolution: UGCVideoResolution;
  avatarScale: number;
  avatarOffsetY: number;
  /** @deprecated Replaced by `avatarScale` (unified across HeyGen + Creatomate + preview).
   *  Older drafts may still have it; readers should fall back to `avatarZoom ?? avatarScale`. */
  avatarZoom?: number;
  avatarStyle: UGCAvatarStyle;
  avatarMatting: boolean;

  // Generated avatar/base video (so a draft saved after generation restores the video,
  // not just the settings). Optional for backward-compat with older drafts.
  generatedVideoId?: string | null;
  generatedVideoUrl?: string | null;
  baseVideoDurationSeconds?: number | null;
  videoStatus?: string | null;

  // Step 4 — Edit Video
  timelineSegments: MediaSegment[];
  textOverlays: TextOverlay[];
  musicPresetId: string;
  customMusicUrl: string;
  musicMixVolumePercent: number;
  captionsEnabled: boolean;
  captionDesign: string;
  captionSpacing: string;

  // Step 5 — Finalize
  selectedPlatforms: string[];
  generatedCaption: string;
  tags: string[];
}

export interface UGCVideoStreamData {
  type: "ugc_video";
  video_id: string;
  status: string;
  cloudfront_url?: string;
  customer_id?: string;
  error?: string;
}
